import { useState } from 'react';
import { EyeIcon, EyeOffIcon } from 'lucide-react';
import { TextInput, type TextInputProps } from '@bcecloud/portal-ui';

export type PasswordInputProps = Omit<TextInputProps, 'type'>;

/**
 * A password field with a show/hide toggle — typing a password blind is how
 * typos become lockouts, so every password field here gets the eye.
 *
 * The toggle is a real button (keyboard reachable) that never submits the
 * form, and it announces its state for screen readers.
 *
 * @param props - Standard text-input props, minus `type`.
 * @returns The password field.
 */
export function PasswordInput({ className, ...rest }: PasswordInputProps): React.ReactElement {
  const [visible, setVisible] = useState(false);

  return (
    // The input's default top margin moves to the wrapper so the toggle's
    // vertical centering is true to the visible field, not the box + margin.
    <div className="relative mt-1.5">
      <TextInput
        {...rest}
        type={visible ? 'text' : 'password'}
        className={`mt-0 pr-11 ${className ?? ''}`}
      />
      <button
        type="button"
        aria-label={visible ? 'Hide password' : 'Show password'}
        aria-pressed={visible}
        onClick={() => setVisible((current) => !current)}
        className="absolute top-1/2 right-1.5 grid size-[34px] -translate-y-1/2 cursor-pointer place-items-center rounded-portal-button text-portal-muted hover:bg-portal-bg hover:text-portal-ink"
      >
        {visible ? (
          <EyeOffIcon aria-hidden className="size-4" />
        ) : (
          <EyeIcon aria-hidden className="size-4" />
        )}
      </button>
    </div>
  );
}
